import {
  SubAccountPermissionApiResponse,
  SubAccountPermissionItem,
  SubAccountPermissionResponse,
  SubAccountPermissionSection,
  SubAccountPermissionSectionApi,
} from './VoPayApi.types';

/**
 * Normalizes a nested permissions tree — `GET /account/subaccount/permissions` and
 * `GET /partner/account/permissions`, both with `Version=new` — from its wire shape (sections keyed by
 * slug, items and `Dependencies` as JSON_FORCE_OBJECT numeric-keyed objects) into `SubAccountPermissionSection[]`.
 *
 * The backend only sends kebab-case section keys, so `SectionName` is just an English fallback label for
 * when the host has no translation. Match on `SectionId` (the raw key), never on `SectionName`.
 */
export default function toPermissionTree(resp: SubAccountPermissionApiResponse): SubAccountPermissionResponse {
  const kebabToTitleCase = (str: string): string =>
    str
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  // A permission section has numeric keys whose values carry a `Permission`; anything else is a branch.
  const isPermissionSection = (obj: unknown): obj is SubAccountPermissionSectionApi => {
    if (!obj || typeof obj !== 'object') return false;
    const keys = Object.keys(obj);
    if (keys.length === 0) return false;
    if (!keys.every((key) => /^\d+$/.test(key))) return false;

    const firstValue = (obj as Record<string, unknown>)[keys[0]];

    return typeof firstValue === 'object' && firstValue !== null && 'Permission' in firstValue;
  };

  const transformStructure = (obj: unknown): SubAccountPermissionSection[] | SubAccountPermissionItem[] => {
    if (!obj || typeof obj !== 'object') return [];

    if (isPermissionSection(obj)) {
      return Object.values(obj).map((item) => ({
        ...item,
        Dependencies: Object.values(item.Dependencies || {}),
      }));
    }

    return Object.entries(obj).map(([key, value]) => ({
      SectionId: key,
      SectionName: kebabToTitleCase(key),
      SubItems: transformStructure(value),
    }));
  };

  return {
    ...resp,
    PermissionValues: transformStructure(resp.PermissionValues) as SubAccountPermissionSection[],
  };
}
